import React, { useEffect, useState } from "react";
import { ContentSection } from "../Layout/ContentSection";
import { api } from "../api";
import { useUser, useUserStatus } from "../state";
import { RLink } from "../DesignSystem/RLink";
import { Button } from "../DesignSystem/Button";

type SupportUser = {
  id: number;
  username: string;
  avatar_url: string;
  experience: number;
};

export const SupportPage = (): JSX.Element => {
  const userState = useUser();
  const { refreshUser } = useUserStatus();
  const [users, setUsers] = useState<SupportUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [assuming, setAssuming] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      const response = await api.get("/api/support/users");
      if (Array.isArray(response)) {
        setUsers(response);
      }
      setLoading(false);
    };

    fetchUsers();
  }, []);

  if (userState.type !== "logged-in" || userState.user.id !== 1) {
    return (
      <ContentSection>
        <div className="p-8 text-center">
          <h1 className="text-xl font-bold">Access Denied</h1>
          <p className="mt-2 text-slate-600 dark:text-slate-400">
            This page is only available to administrators.
          </p>
        </div>
      </ContentSection>
    );
  }

  const handleAssume = async (userId: number) => {
    setAssuming(true);
    await api.post(`/api/support/assume/${userId}`, {});
    await refreshUser();
    setAssuming(false);
  };

  const filteredUsers = users.filter((user) =>
    user.username.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <ContentSection>
        <div className="p-8 text-center">Loading users...</div>
      </ContentSection>
    );
  }

  return (
    <ContentSection>
      <div className="p-6">
        <h1 className="mb-6 text-2xl font-bold">Support - User Impersonation</h1>

        <div className="mb-6">
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-md rounded-lg border border-slate-300 px-4 py-2 text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:placeholder:text-slate-500"
          />
        </div>

        <div className="grid gap-2">
          {filteredUsers.map((user) => (
            <div
              key={user.id}
              className="flex items-center justify-between rounded-lg border border-slate-200 p-3 dark:border-slate-700"
            >
              <div className="flex items-center gap-3">
                {user.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.username}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700">
                    <span className="text-sm font-medium">
                      {user.username[0]?.toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <RLink
                    to={`/user/${user.username}`}
                    noUnderline
                  >
                    <span className="font-medium text-slate-900 hover:underline dark:text-white">
                      {user.username}
                    </span>
                  </RLink>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    ID: {user.id} | XP: {user.experience}
                  </div>
                </div>
              </div>

              <div className="shrink-0">
                <Button
                  disabled={assuming}
                  onClick={() => handleAssume(user.id)}
                  grow={false}
                  variant="primary"
                >
                  {assuming ? "..." : "Assume"}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {filteredUsers.length === 0 && (
          <div className="mt-4 text-center text-slate-500 dark:text-slate-400">
            No users found matching "{search}"
          </div>
        )}
      </div>
    </ContentSection>
  );
};
